import { DEFAULT_MEDIA_QUERY_SIZES } from 'common/constants/story-media-query';
import type { StoryMediaPlatform } from 'types/story';
import { EventEmitter } from 'utils/event-emitter';
import { ZOOM_MIN, ZOOM_STEP } from '../../DimensionControls/constants';

export type ResizerSetDisabledEvent = { type: 'setDisabled'; data: boolean };

export type ResizerSetCardHeightEvent = { type: 'setCardHeight'; data: number };

export type ResizerMousedownEvent = { type: 'resizerMousedown'; data: { pageX: number } };

type ResizerEvent = ResizerSetDisabledEvent | ResizerSetCardHeightEvent | ResizerMousedownEvent;

export const ResizerEvEm = new EventEmitter<ResizerEvent>();

interface GetInitialPlatformWidthProps {
	platform: StoryMediaPlatform;
	min: number;
	max: number;
	containerWidth: number;
}

export function getInitialPlatformWidth(props: GetInitialPlatformWidthProps): string {
	const containerOffset = 0;

	switch (props.platform) {
		case 'desktop':
		case 'tablet':
			return `${Math.max(props.min, Math.min(props.containerWidth - containerOffset, props.max))}px`;
		case 'mobileLandscape':
			return `${Math.min(DEFAULT_MEDIA_QUERY_SIZES.mobileLandscape.referenceWidth, props.max)}px`;
		case 'mobile':
			return `${Math.min(DEFAULT_MEDIA_QUERY_SIZES.mobile.referenceWidth, props.max)}px`;
		default:
			return '';
	}
}

export function getLogicalCanvasSize({
	requestedWidth,
	boundaries,
	ratio,
}: {
	requestedWidth: number;
	boundaries: { min: number; max: number };
	ratio: number;
}) {
	const width = Math.max(boundaries.min, Math.min(requestedWidth, boundaries.max));

	return { width, height: width / ratio };
}

export function getResizeBoundaries({
	boundaries,
	containerWidth,
}: {
	boundaries: { min: number; max: number };
	containerWidth: number;
}) {
	return {
		min: boundaries.min,
		max: Math.max(boundaries.min, Math.min(boundaries.max, containerWidth)),
	};
}

const RESIZER_SNAP_THRESHOLD = 5;

const RULER_MEDIA_QUERY_LABELS: Record<string, string> = {
	mobile: 'Mobile',
	mobileLandscape: 'Mobile landscape',
	tablet: 'Tablet',
	desktop: 'Desktop',
	fullHD: 'Full HD',
};

export function getSnappedWidth({
	width,
	points,
	boundaries,
}: {
	width: number;
	points: number[];
	boundaries: { min: number; max: number };
}) {
	let snappedWidth = width;
	let closestDistance = RESIZER_SNAP_THRESHOLD;

	points.forEach(point => {
		const distance = Math.abs(point - width);
		const isInRange = point >= boundaries.min && point <= boundaries.max;

		if (isInRange && distance < closestDistance) {
			snappedWidth = point;
			closestDistance = distance;
		}
	});

	return snappedWidth;
}

export function getResizeTargetWidth({
	currentWidth,
	startPageX,
	currentPageX,
	resizeFrom,
	boundaries,
	points,
}: {
	currentWidth: number;
	startPageX: number;
	currentPageX: number;
	resizeFrom: 'left' | 'right';
	boundaries: { min: number; max: number };
	points: number[];
}) {
	const pointerDelta = (currentPageX - startPageX) * 2;
	const requestedWidth = resizeFrom === 'left' ? currentWidth - pointerDelta : currentWidth + pointerDelta;
	const width = Math.max(boundaries.min, Math.min(boundaries.max, requestedWidth));

	return getSnappedWidth({ width, points, boundaries });
}

export function getOverlayHorizontalBounds({
	canvasLeft,
	canvasRight,
	containerLeft,
	scrollLeft,
}: {
	canvasLeft: number;
	canvasRight: number;
	containerLeft: number;
	scrollLeft: number;
}) {
	return {
		left: canvasLeft - containerLeft + scrollLeft,
		right: canvasRight - containerLeft + scrollLeft,
	};
}

export function getRulerMediaQueryLabel(key: string, fallback: string) {
	return RULER_MEDIA_QUERY_LABELS[key] || fallback;
}

export function getToggledCanvasWidth({
	currentWidth,
	boundaries,
	containerWidth,
}: {
	currentWidth: number;
	boundaries: { min: number; max: number };
	containerWidth: number;
}) {
	const effectiveBoundaries = getResizeBoundaries({ boundaries, containerWidth });

	return currentWidth === effectiveBoundaries.max ? effectiveBoundaries.min : effectiveBoundaries.max;
}

export const RESIZER_DRAG_THRESHOLD = 3;

export function hasResizerDragStarted(startPageX: number, currentPageX: number) {
	return Math.abs(currentPageX - startPageX) >= RESIZER_DRAG_THRESHOLD;
}

export function getFitZoom({ logicalWidth, containerWidth }: { logicalWidth: number; containerWidth: number }) {
	return logicalWidth ? Math.min(1, containerWidth / logicalWidth) : 1;
}

export function getCenteredScrollLeft({
	scrollLeft,
	logicalWidth,
	previousZoom,
	nextZoom,
	previousContainerWidth,
	nextContainerWidth,
}: {
	scrollLeft: number;
	logicalWidth: number;
	previousZoom: number;
	nextZoom: number;
	previousContainerWidth: number;
	nextContainerWidth: number;
}) {
	const previousStageWidth = logicalWidth * previousZoom;
	const logicalCenter =
		previousStageWidth <= previousContainerWidth
			? logicalWidth / 2
			: (scrollLeft + previousContainerWidth / 2) / previousZoom;
	const targetScrollLeft = logicalCenter * nextZoom - nextContainerWidth / 2;
	const maxScrollLeft = Math.max(0, logicalWidth * nextZoom - nextContainerWidth);

	return Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));
}

export function getZoomOut(zoom: number) {
	return zoom <= ZOOM_MIN ? zoom : Math.max(ZOOM_MIN, zoom - ZOOM_STEP);
}
